/* Grade da Coleção — sections/tp-e-main-collection-v1.liquid
   Filtros, ordenação e paginação via Section Rendering API (sem recarregar a página),
   gaveta de filtros no mobile e alternância de colunas (salva no navegador). */

if (!customElements.get('tp-main-collection')) {
  customElements.define(
    'tp-main-collection',
    class TpMainCollection extends HTMLElement {
      static VIEW_STORAGE_KEY = 'tp-main-collection-view';
      static DESKTOP_QUERY = '(min-width: 1024px)';

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.collectionUrl = this.dataset.collectionUrl;
        this.drawer = this.querySelector('[data-tp-collection-drawer]');
        this.overlay = this.querySelector('.tp-e-main-collection__overlay');
        this.openButton = this.querySelector('[data-tp-collection-open]');
        this.view = this.readView();

        this.onChange = this.onChange.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onPopState = this.onPopState.bind(this);
        this.onBreakpointChange = this.onBreakpointChange.bind(this);

        this.addEventListener('change', this.onChange);
        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKeydown);
        window.addEventListener('popstate', this.onPopState);
        this.desktopQuery = window.matchMedia(TpMainCollection.DESKTOP_QUERY);
        this.desktopQuery.addEventListener('change', this.onBreakpointChange);

        this.applyView();
        this.syncPriceSelection();
      }

      disconnectedCallback() {
        window.removeEventListener('popstate', this.onPopState);
        this.desktopQuery?.removeEventListener('change', this.onBreakpointChange);
        document.documentElement.classList.remove('tp-e-main-collection-scroll-lock');
      }

      /* ---------- Eventos ---------- */

      onChange(event) {
        const target = event.target;
        if (target.matches('[data-tp-collection-sort]') || target.closest('[data-tp-collection-form]')) {
          if (target.name === 'tp-price') this.lastPrice = target.value;
          this.update(this.buildUrl());
        }
      }

      onClick(event) {
        const priceInput = event.target.closest('input[name="tp-price"]');
        if (priceInput) {
          // Clicar de novo na faixa de preço já marcada desmarca o filtro
          if (this.lastPrice === priceInput.value) {
            priceInput.checked = false;
            this.lastPrice = null;
            this.update(this.buildUrl());
          }
          return;
        }

        const link = event.target.closest('[data-tp-collection-link]');
        if (link) {
          event.preventDefault();
          this.update(link.href, { scroll: link.hasAttribute('data-tp-collection-scroll') });
          return;
        }

        const viewButton = event.target.closest('[data-tp-collection-view]');
        if (viewButton) {
          this.view[viewButton.dataset.tpCollectionView] = viewButton.dataset.columns;
          this.saveView();
          this.applyView();
          return;
        }

        if (event.target.closest('[data-tp-collection-open]')) {
          this.openDrawer();
          return;
        }

        if (event.target.closest('[data-tp-collection-close]')) this.closeDrawer();
      }

      onKeydown(event) {
        if (event.key === 'Escape' && this.isDrawerOpen()) {
          event.preventDefault();
          this.closeDrawer();
        }
      }

      onPopState() {
        this.update(window.location.href, { push: false });
      }

      onBreakpointChange(event) {
        if (event.matches && this.isDrawerOpen()) this.closeDrawer({ restoreFocus: false });
      }

      /* ---------- URL e atualização ---------- */

      buildUrl() {
        const params = new URLSearchParams();
        const form = this.querySelector('[data-tp-collection-form]');

        if (form) {
          for (const [key, value] of new FormData(form)) {
            if (key !== 'tp-price') params.append(key, value);
          }

          const price = form.querySelector('input[name="tp-price"]:checked');
          if (price) {
            const group = price.closest('[data-param-min]');
            const [min, max] = price.value.split(':');
            if (min) params.set(group.dataset.paramMin, min);
            if (max) params.set(group.dataset.paramMax, max);
          }
        }

        const sort = this.querySelector('[data-tp-collection-sort]');
        if (sort && sort.value) params.set('sort_by', sort.value);

        const query = params.toString();
        return query ? `${this.collectionUrl}?${query}` : this.collectionUrl;
      }

      cleanUrl(url) {
        const parsed = new URL(url, window.location.origin);
        parsed.searchParams.delete('section_id');
        return `${parsed.pathname}${parsed.search}`;
      }

      async update(url, { scroll = false, push = true } = {}) {
        const pageUrl = this.cleanUrl(url);
        const fetchUrl = new URL(pageUrl, window.location.origin);
        fetchUrl.searchParams.set('section_id', this.sectionId);

        this.abortController?.abort();
        this.abortController = new AbortController();
        this.setLoading(true);

        try {
          const response = await fetch(fetchUrl, { signal: this.abortController.signal });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const html = await response.text();
          this.render(new DOMParser().parseFromString(html, 'text/html'));
          if (push) window.history.pushState({}, '', pageUrl);
          if (scroll) this.scrollToProducts();
          this.setLoading(false);
        } catch (error) {
          if (error.name === 'AbortError') return;
          window.location.href = pageUrl;
        }
      }

      render(doc) {
        const activeId = document.activeElement?.id;
        const closedFilters = Array.from(this.querySelectorAll('details[data-filter]:not([open])')).map(
          (details) => details.dataset.filter
        );

        this.querySelectorAll('[data-tp-collection-part]').forEach((part) => {
          const next = doc.querySelector(`[data-tp-collection-part="${part.dataset.tpCollectionPart}"]`);
          if (next) part.innerHTML = next.innerHTML;
        });

        const nextSort = doc.querySelector('[data-tp-collection-sort]');
        const sort = this.querySelector('[data-tp-collection-sort]');
        if (nextSort && sort) sort.value = nextSort.value;

        closedFilters.forEach((key) => {
          this.querySelector(`details[data-filter="${CSS.escape(key)}"]`)?.removeAttribute('open');
        });

        this.applyView();
        this.syncPriceSelection();

        const results = this.querySelector('[data-tp-collection-part="results"]');
        if (results && typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(results);
        }

        if (activeId) document.getElementById(activeId)?.focus({ preventScroll: true });
      }

      setLoading(isLoading) {
        const results = this.querySelector('[data-tp-collection-part="results"]');
        if (!results) return;
        results.classList.toggle('tp-e-main-collection__results--loading', isLoading);
        results.setAttribute('aria-busy', String(isLoading));
      }

      scrollToProducts() {
        const products = this.querySelector('.tp-e-main-collection__products');
        if (!products) return;
        const top = products.getBoundingClientRect().top + window.scrollY - 120;
        if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
      }

      syncPriceSelection() {
        this.lastPrice = this.querySelector('input[name="tp-price"]:checked')?.value ?? null;
      }

      /* ---------- Colunas ---------- */

      readView() {
        const view = { mobile: this.dataset.defaultMobile, desktop: this.dataset.defaultDesktop };
        try {
          const saved = JSON.parse(window.localStorage.getItem(TpMainCollection.VIEW_STORAGE_KEY));
          if (['1', '2'].includes(saved?.mobile)) view.mobile = saved.mobile;
          if (['3', '4'].includes(saved?.desktop)) view.desktop = saved.desktop;
        } catch (error) {
          // Sem acesso ao localStorage: segue com o padrão da seção
        }
        return view;
      }

      saveView() {
        try {
          window.localStorage.setItem(TpMainCollection.VIEW_STORAGE_KEY, JSON.stringify(this.view));
        } catch (error) {
          // Preferência não persistida
        }
      }

      applyView() {
        const grid = this.querySelector('[data-tp-collection-grid]');
        if (grid) {
          grid.dataset.colsMobile = this.view.mobile;
          grid.dataset.colsDesktop = this.view.desktop;
        }
        this.querySelectorAll('[data-tp-collection-view]').forEach((button) => {
          const pressed = this.view[button.dataset.tpCollectionView] === button.dataset.columns;
          button.setAttribute('aria-pressed', String(pressed));
        });
      }

      /* ---------- Gaveta de filtros (mobile) ---------- */

      isDrawerOpen() {
        return Boolean(this.drawer?.classList.contains('tp-e-main-collection__filters--open'));
      }

      openDrawer() {
        if (!this.drawer) return;
        this.drawer.setAttribute('role', 'dialog');
        this.drawer.setAttribute('aria-modal', 'true');
        this.drawer.classList.add('tp-e-main-collection__filters--open');
        this.openButton?.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('tp-e-main-collection-scroll-lock');

        if (this.overlay) {
          this.overlay.hidden = false;
          requestAnimationFrame(() => this.overlay.classList.add('tp-e-main-collection__overlay--visible'));
        }

        const closeButton = this.drawer.querySelector('.tp-e-main-collection__drawer-close');
        if (typeof trapFocus === 'function') {
          trapFocus(this.drawer, closeButton);
        } else {
          closeButton?.focus();
        }
      }

      closeDrawer({ restoreFocus = true } = {}) {
        if (!this.drawer) return;
        this.drawer.classList.remove('tp-e-main-collection__filters--open');
        this.drawer.removeAttribute('role');
        this.drawer.removeAttribute('aria-modal');
        this.openButton?.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('tp-e-main-collection-scroll-lock');

        if (this.overlay) {
          this.overlay.classList.remove('tp-e-main-collection__overlay--visible');
          this.overlay.hidden = true;
        }

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(restoreFocus ? this.openButton : null);
        } else if (restoreFocus) {
          this.openButton?.focus();
        }
      }
    }
  );
}
